import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { iniciativasAPI } from '../services/api';
import './KanbanOperaciones.css';

// Componente para la tarjeta de iniciativa en operaciones
const IniciativaCardOperaciones = ({ 
  iniciativa, 
  onDragStart, 
  isDragging,
  // Props de filtros para navegación
  searchTerm = '',
  estadosFiltro = [],
  areasFiltro = [],
  jefesFiltro = [],
  lineaNegocioFiltro = [],
  negocioFiltro = [],
  negociosFiltro = [],
  innovacionFiltro = 'todos',
  tipoInnovacionFiltro = 'todos',
  añosFiltro = []
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  
  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', iniciativa.id);
    e.dataTransfer.setData('application/json', JSON.stringify(iniciativa));
    onDragStart(iniciativa);
  };

  const handleDragEnd = (e) => {
    onDragStart(null);
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    }).format(amount);
  };

  const getProgressColor = (estado) => {
    const colors = {
      'aprobado': '#28a745',
      'planificada': '#17a2b8',
      'en_curso': '#007bff',
      'bloqueada': '#ffc107',
      'cancelada': '#dc3545',
      'finalizada': '#6f42c1'
    };
    return colors[iniciativa.estado] || '#6c757d';
  };

  return (
    <div
      className={`iniciativa-card-operaciones ${isDragging ? 'dragging' : ''}`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-estado={iniciativa.estado}
      style={{
        backgroundColor: isHovered && !isDragging ? '#f8f9fa' : '#fff',
        border: isDragging ? '2px solid #495057' : (isHovered ? '1px solid #adb5bd' : '1px solid #d1d7dc'),
        borderRadius: '6px',
        padding: '8px',
        margin: '4px 0',
        fontSize: '12px',
        boxShadow: isDragging 
          ? '0 8px 16px rgba(0,0,0,0.25), 0 0 0 3px rgba(0,0,0,0.1)' 
          : (isHovered ? '0 3px 8px rgba(0,0,0,0.15)' : '0 1px 2px rgba(0,0,0,0.1)'),
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        minHeight: '90px',
        maxHeight: '110px',
        overflow: 'hidden',
        opacity: isDragging ? 0.7 : 1,
        transform: isDragging 
          ? 'rotate(-2deg) scale(1.05)' 
          : (isHovered ? 'translateY(-2px) scale(1.01)' : 'translateY(0) scale(1)'),
        borderLeft: `3px solid ${getProgressColor(iniciativa.estado)}`
      }}
    >
      <div style={{ 
        marginBottom: '6px', 
        borderBottom: '1px solid #e1e4e8', 
        paddingBottom: '4px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <h6 style={{ 
          fontSize: '11px', 
          fontWeight: '600', 
          margin: '0', 
          lineHeight: '1.2',
          color: '#24292e',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          flex: 1,
          marginRight: '4px'
        }}>
          {iniciativa.nombre_de_la_oportunidad}
        </h6>
        <div 
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: getProgressColor(iniciativa.estado),
            flexShrink: 0,
            marginTop: '2px'
          }}
        />
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '9px', color: '#6a737d', fontWeight: '500' }}>Jefe Proyecto:</span>
          <span style={{ 
            fontSize: '9px', 
            color: '#24292e',
            textAlign: 'right',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '120px',
            whiteSpace: 'nowrap',
            fontWeight: '500'
          }}>{iniciativa.jefe_proyecto_nombre || 'Sin asignar'}</span>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '9px', color: '#6a737d', fontWeight: '500' }}>Fecha fin:</span>
          <span style={{ 
            fontSize: '9px', 
            color: '#24292e',
            textAlign: 'right',
            fontWeight: '500'
          }}>
            {iniciativa.fecha_de_fin_planificada ? 
              new Date(iniciativa.fecha_de_fin_planificada).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) : 
              'N/A'
            }
          </span>
        </div>
        
        {/* Botón compacto */}
        <div style={{ marginTop: '6px', textAlign: 'center' }}>
          <Link 
            to={(() => {
              // Construir parámetros de estado actual
              const currentState = {
                from: 'operaciones',
                tab: '0', // Tab de kanban
                search: searchTerm || '',
                estados: estadosFiltro.join(','),
                areas: areasFiltro.join(','),
                jefes: jefesFiltro.join(','),
                lineaNegocio: lineaNegocioFiltro.join(','),
                negocio: negocioFiltro.join(','),
                negocios: negociosFiltro.join(','),
                innovacion: innovacionFiltro,
                tipoInnovacion: tipoInnovacionFiltro,
                años: añosFiltro.join(',')
              };
              
              // Filtrar parámetros vacíos
              const filteredParams = Object.fromEntries(
                Object.entries(currentState).filter(([key, value]) => value && value !== '')
              );
              
              const queryString = new URLSearchParams(filteredParams).toString();
              return `/projects/${iniciativa.id}?${queryString}`;
            })()} 
            onClick={(e) => e.stopPropagation()}
            style={{
              fontSize: '8px',
              padding: '2px 6px',
              backgroundColor: '#f1f3f4',
              color: '#1976d2',
              textDecoration: 'none',
              borderRadius: '3px',
              border: '1px solid #d1d7dc',
              display: 'inline-block',
              fontWeight: '500'
            }}
          >
            👁 Ver
          </Link>
        </div>
      </div>
    </div>
  );
};

// Componente para la columna del Kanban Operaciones
const KanbanColumnOperaciones = ({ 
  estado, 
  titulo, 
  iniciativas, 
  onDrop, 
  onDragOver, 
  draggedItem, 
  onDragStart,
  // Props de filtros
  searchTerm = '',
  estadosFiltro = [],
  areasFiltro = [],
  jefesFiltro = [],
  lineaNegocioFiltro = [],
  negocioFiltro = [],
  negociosFiltro = [],
  innovacionFiltro = 'todos',
  tipoInnovacionFiltro = 'todos',
  añosFiltro = []
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const isEmpty = iniciativas.length === 0;

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const iniciativaData = JSON.parse(e.dataTransfer.getData('application/json'));
    onDrop(iniciativaData, estado);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
    onDragOver(e);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Solo desactivar isDragOver si realmente salimos del elemento y no solo pasamos a un hijo
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  };

  const getColumnColor = (estado) => {
    const colors = {
      'aprobado': '#d4edda',
      'planificada': '#d1ecf1',
      'en_curso': '#cce5ff',
      'bloqueada': '#fff3cd',
      'cancelada': '#f8d7da',
      'finalizada': '#e2d9f3'
    };
    return colors[estado] || '#f8f9fa';
  };

  return (
    <div 
      className={`kanban-column-operaciones ${isDragOver ? 'drag-over' : ''} ${isEmpty ? 'empty-column' : ''}`}
      style={{ 
        backgroundColor: isEmpty ? '#fafbfc' : getColumnColor(estado),
        border: isDragOver ? '3px dashed #5a6268' : '1px solid #d1d7dc',
        borderRadius: '6px',
        margin: '0 4px',
        minWidth: isEmpty ? (isDragOver ? '180px' : '60px') : (isDragOver ? '230px' : '190px'),
        maxWidth: isEmpty ? (isDragOver ? '240px' : '60px') : (isDragOver ? '270px' : '210px'),
        width: isEmpty ? (isDragOver ? '200px' : '60px') : (isDragOver ? '240px' : '190px'),
        height: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: isDragOver 
          ? '0 8px 24px rgba(0,0,0,0.3), 0 0 0 4px rgba(0,0,0,0.08)' 
          : '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isEmpty && !isDragOver ? 0.6 : 1,
        transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
        zIndex: isDragOver ? 10 : 1
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      title={isEmpty ? `${titulo} - Vacía` : titulo}
    >
      <div style={{
        padding: isEmpty && !isDragOver ? '6px 4px' : '8px 10px',
        borderBottom: '1px solid #e1e4e8',
        backgroundColor: isDragOver ? '#495057' : '#f6f8fa',
        borderRadius: '6px 6px 0 0',
        flexShrink: 0,
        writingMode: isEmpty && !isDragOver ? 'vertical-rl' : 'horizontal-tb',
        textOrientation: isEmpty && !isDragOver ? 'mixed' : 'unset',
        transition: 'all 0.3s ease'
      }}>
        {isEmpty && !isDragOver ? (
          // Vista minimizada para columnas vacías
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: '4px',
            height: '100px'
          }}>
            <span style={{
              fontSize: '9px',
              fontWeight: '600',
              color: '#6a737d',
              textAlign: 'center',
              lineHeight: '1.1',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              whiteSpace: 'nowrap'
            }}>
              {titulo}
            </span>
            <span style={{
              backgroundColor: '#6a737d',
              color: 'white',
              fontSize: '8px',
              fontWeight: '600',
              padding: '2px 4px',
              borderRadius: '8px',
              minWidth: '14px',
              textAlign: 'center'
            }}>
              0
            </span>
          </div>
        ) : (
          // Vista normal para columnas con contenido
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 style={{ 
              fontSize: isDragOver ? '14px' : '12px',
              fontWeight: '600',
              margin: '0',
              color: isDragOver ? '#ffffff' : '#24292e',
              lineHeight: '1.2',
              transition: 'all 0.3s ease'
            }}>
              {titulo}
              {isDragOver && <span style={{ marginLeft: '8px', fontSize: '14px' }}>⬇️</span>}
            </h5>
            <span style={{
              backgroundColor: isDragOver ? '#ffffff' : '#6a737d',
              color: isDragOver ? '#495057' : 'white',
              fontSize: '9px',
              fontWeight: '600',
              padding: '2px 6px',
              borderRadius: '10px',
              minWidth: '18px',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}>
              {iniciativas.length}
            </span>
          </div>
        )}
      </div>
      
      {(!isEmpty || isDragOver) && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '6px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#d1d7dc transparent',
          backgroundColor: isDragOver ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
          border: isDragOver ? '2px dashed rgba(0, 0, 0, 0.2)' : 'none',
          borderRadius: isDragOver ? '4px' : '0',
          margin: isDragOver ? '4px' : '0',
          transition: 'all 0.3s ease',
          minHeight: isDragOver && isEmpty ? '100px' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isDragOver && isEmpty ? 'center' : 'stretch',
          justifyContent: isDragOver && isEmpty ? 'center' : 'flex-start'
        }}>
          {isDragOver && isEmpty && (
            <div style={{
              textAlign: 'center',
              color: '#495057',
              fontSize: '12px',
              fontWeight: '500',
              padding: '20px'
            }}>
              ⬇️ Soltar aquí
            </div>
          )}
          {iniciativas.map(iniciativa => (
            <IniciativaCardOperaciones
              key={iniciativa.id}
              iniciativa={iniciativa}
              onDragStart={onDragStart}
              isDragging={draggedItem && draggedItem.id === iniciativa.id}
              searchTerm={searchTerm}
              estadosFiltro={estadosFiltro}
              areasFiltro={areasFiltro}
              jefesFiltro={jefesFiltro}
              lineaNegocioFiltro={lineaNegocioFiltro}
              negocioFiltro={negocioFiltro}
              negociosFiltro={negociosFiltro}
              innovacionFiltro={innovacionFiltro}
              tipoInnovacionFiltro={tipoInnovacionFiltro}
              añosFiltro={añosFiltro}
            />
          ))}
        </div>
      )}
    </div>
  );
};


// Ahora acepta añosFiltro como prop para filtrar por años externos
const KanbanOperaciones = ({ 
  embedded = false, 
  añosFiltro = [new Date().getFullYear()],
  searchTerm = '',
  estadosFiltro = [],
  areasFiltro = [],
  jefesFiltro = [],  
  lineaNegocioFiltro = [],
  negocioFiltro = [],  
  negociosFiltro = [],
  innovacionFiltro = [],
  tipoInnovacionFiltro = [],
  tipoFiltro = [],
  madurezFiltro = [],
  iniciativas: iniciativasFromParent = null,
  loading: loadingFromParent = false
}) => {
  const [iniciativas, setIniciativas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  // Los años seleccionados ahora vienen de props, si no usa 2026 por defecto
  const añosSeleccionados = añosFiltro && añosFiltro.length > 0 ? añosFiltro : [2026];

  // Estados operativos del Kanban
  const estadosOperativos = [
    { key: 'aprobado', label: 'Aprobado' },
    { key: 'planificada', label: 'Planificada' },
    { key: 'en_curso', label: 'En curso' },
    { key: 'bloqueada', label: 'Bloqueada' },
    { key: 'cancelada', label: 'Cancelada' },
    { key: 'finalizada', label: 'Finalizada' }
  ];

  useEffect(() => {
    if (iniciativasFromParent) {
      // Si recibimos iniciativas del componente padre, las usamos directamente
      setIniciativas(iniciativasFromParent);
      setLoading(false);
    } else {
      // Solo cargar datos si no los recibimos del padre
      loadIniciativas();
    }
  }, [iniciativasFromParent]);

  // Actualizar loading state basado en el padre si está disponible
  useEffect(() => {
    if (iniciativasFromParent !== null) {
      setLoading(loadingFromParent);
    }
  }, [loadingFromParent, iniciativasFromParent]);

  const loadIniciativas = useCallback(async () => {
    try {
      setLoading(true);
      let iniciativasData = [];
      
      try {
        // Usar el endpoint específico para la tabla de operaciones
        const response = await iniciativasAPI.getTablaOperaciones();
        iniciativasData = response.data;
      } catch (errTabla) {
        setError('Error al cargar las iniciativas de operaciones');
        console.error(errTabla);
        iniciativasData = [];
      }
      
      // Filtrar solo iniciativas con estados operativos
      const estadosKeys = estadosOperativos.map(e => e.key);
      const iniciativasFiltradas = iniciativasData.filter(iniciativa => 
        estadosKeys.includes(iniciativa.estado)
      );
      
      setIniciativas(iniciativasFiltradas);
    } catch (err) {
      setError('Error al cargar las iniciativas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDrop = async (iniciativa, nuevoEstado) => {
    if (iniciativa.estado === nuevoEstado) {
      return; // No hay cambio
    }

    try {
      // Actualizar en el backend usando partialUpdate para solo cambiar el estado
      await iniciativasAPI.partialUpdate(iniciativa.id, { estado: nuevoEstado });
      
      // Actualizar el estado local
      setIniciativas(prev => 
        prev.map(init => 
          init.id === iniciativa.id 
            ? { ...init, estado: nuevoEstado }
            : init
        )
      );
      
      // Limpiar el item arrastrado
      setDraggedItem(null);
    } catch (err) {
      console.error('Error al actualizar el estado de la iniciativa:', err);
      setError(`Error al actualizar el estado: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragStart = (iniciativa) => {
    setDraggedItem(iniciativa);
  };


  // Función para obtener el año de una iniciativa
  const getAñoIniciativa = (iniciativa) => {
    if (iniciativa.año) {
      return iniciativa.año;
    }
    if (iniciativa.fecha_de_inicio_real) {
      return new Date(iniciativa.fecha_de_inicio_real).getFullYear();
    } else if (iniciativa.fecha_de_inicio_planificada) {
      return new Date(iniciativa.fecha_de_inicio_planificada).getFullYear();
    } else if (iniciativa.created_at) {
      return new Date(iniciativa.created_at).getFullYear();
    }
    return new Date().getFullYear();
  };


  // Obtener años únicos disponibles (no se usa si el año viene de Operaciones)
  const getAñosDisponibles = () => {
    const años = iniciativas.map(iniciativa => getAñoIniciativa(iniciativa));
    return [...new Set(años)].sort((a, b) => b - a);
  };


  // Filtrar por estado y años seleccionados - Memoizado
  const getIniciativasPorEstado = useCallback((estado) => {
    // Si recibimos iniciativas del padre (modo embedded),
    // las iniciativas ya vienen filtradas desde Operaciones.js por todos los filtros
    // (años, estados, areas, jefes, negocios, innovación, tipo, madurez, etc.)
    // Solo filtramos por estado del Kanban y aplicamos filtro de texto si es necesario
    if (iniciativasFromParent) {
      return (iniciativas || []).filter(i => i.estado === estado && (
        searchTerm === '' ||
        i.nombre_de_la_oportunidad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.negocio_estructura?.negocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.negocio_estructura?.linea_de_negocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.negocio_estructura?.subnegocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.tecnologia_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.jefe_proyecto_nombre?.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    }
    // Si no, aplicar el filtrado completo (modo standalone)
    return iniciativas.filter(iniciativa => {
      // Filtro por estado del Kanban
      if (iniciativa.estado !== estado) return false;
      
      // Filtro por años
      const añoIniciativa = getAñoIniciativa(iniciativa);
      if (!añosSeleccionados.includes(añoIniciativa)) return false;
      
      // Filtros adicionales de búsqueda y selecciones - usar arrays vacíos para "todos"
      if (estadosFiltro.length > 0 && !estadosFiltro.includes(iniciativa.estado)) return false;
      if (areasFiltro.length > 0 && !areasFiltro.includes(iniciativa.tecnologia_nombre)) return false;
      if (jefesFiltro.length > 0 && !jefesFiltro.includes(iniciativa.jefe_proyecto_nombre)) return false;
      // Filtros de jerarquía de negocio - aplicar en cascada respetando la jerarquía
      const lineaNegocio = iniciativa.negocio_estructura?.linea_de_negocio || iniciativa.linea_de_negocio || iniciativa.negocio_estructura_linea_de_negocio;
      const negocio = iniciativa.negocio_estructura?.negocio || iniciativa.negocio || iniciativa.negocio_estructura_negocio;
      const subnegocio = iniciativa.negocio_estructura_nombre;
      
      // Aplicar lógica de cascada normal
      if (negociosFiltro.length > 0) {
        if (!negociosFiltro.includes(subnegocio)) return false;
      } else if (negocioFiltro.length > 0) {
        if (!negocioFiltro.includes(negocio)) return false;
      } else if (lineaNegocioFiltro.length > 0) {
        if (!lineaNegocioFiltro.includes(lineaNegocio)) return false;
      }
      
      // Filtro de texto
      if (searchTerm === '') return true;
      return (
        iniciativa.nombre_de_la_oportunidad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.negocio_estructura?.negocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.negocio_estructura?.linea_de_negocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.negocio_estructura?.subnegocio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.tecnologia_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        iniciativa.jefe_proyecto_nombre?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [iniciativas, añosSeleccionados, estadosFiltro, areasFiltro, jefesFiltro, lineaNegocioFiltro, negocioFiltro, negociosFiltro, searchTerm]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button 
          className="btn btn-outline-danger ms-3" 
          onClick={loadIniciativas}
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Calcular ancho total estimado de las columnas
  const calculateTotalWidth = () => {
    let totalWidth = 0;
    estadosOperativos.forEach(estado => {
      const iniciativasPorEstado = getIniciativasPorEstado(estado.key);
      const isEmpty = iniciativasPorEstado.length === 0;
      totalWidth += isEmpty ? 60 : 190; // 60px para vacías, 190px para con contenido
      totalWidth += 8; // gap entre columnas (4px * 2)
    });
    return totalWidth;
  };

  const totalEstimatedWidth = calculateTotalWidth();
  const shouldCenter = totalEstimatedWidth < (window.innerWidth - 100); // -100px para márgenes


  return (
    <div className={`kanban-operaciones ${embedded ? 'embedded' : ''}`}>
      {/* Header - Solo mostrar si no está embebido */}
      {!embedded && (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>Kanban Operaciones</h1>
          <div className="d-flex gap-3 align-items-center">
            <div className="d-flex align-items-center me-3">
              <label className="form-label me-2 mb-0">Año:</label>
              <select
                className="form-select"
                style={{ width: '120px' }}
                value={añosSeleccionados.length === 1 ? añosSeleccionados[0] : ''}
                onChange={(e) => {/* No-op si añosFiltro viene de fuera */}}
                disabled={añosFiltro && añosFiltro.length > 0}
              >
                {añosSeleccionados.length > 1 ? (
                  <option value="">Múltiples años</option>
                ) : (
                  getAñosDisponibles().map(año => (
                    <option key={año} value={año}>{año}</option>
                  ))
                )}              </select>
            </div>
            <div className="kanban-stats">
              <small className="text-muted">Total iniciativas: {iniciativas.filter(iniciativa => añosSeleccionados.includes(getAñoIniciativa(iniciativa))).length}</small>
            </div>
            <button
              className="btn btn-outline-primary"
              onClick={loadIniciativas}
            >
              🔄 Actualizar
            </button>
          </div>
        </div>
      )}

      <div style={{
        width: '100%',
        height: 'calc(100vh - 200px)',
        overflowX: 'auto',
        overflowY: 'hidden',
        padding: '8px 0'
      }}>
        <div style={{
          display: 'flex',
          gap: '0px',
          minWidth: 'fit-content',
          // Centramos solo si el contenido cabe en el viewport
          justifyContent: shouldCenter ? 'center' : 'flex-start',
          margin: shouldCenter ? '0' : '0 auto',
          height: '100%',
          scrollbarHeight: '8px',
          scrollbarColor: '#d1d7dc transparent'
        }}>
        {estadosOperativos.map(estado => (
          <KanbanColumnOperaciones
            key={estado.key}
            estado={estado.key}
            titulo={estado.label}
            iniciativas={getIniciativasPorEstado(estado.key)}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            draggedItem={draggedItem}
            onDragStart={handleDragStart}
            searchTerm={searchTerm}
            estadosFiltro={estadosFiltro}
            areasFiltro={areasFiltro}
            jefesFiltro={jefesFiltro}
            lineaNegocioFiltro={lineaNegocioFiltro}
            negocioFiltro={negocioFiltro}
            negociosFiltro={negociosFiltro}
            innovacionFiltro={innovacionFiltro}
            tipoInnovacionFiltro={tipoInnovacionFiltro}
            añosFiltro={añosFiltro}
          />
        ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(KanbanOperaciones);